/**
 * The slide sequence for a live game night. The host panel walks this list
 * with Next and Prev, and every entry maps to one projector slide.
 *
 * Pure so that tests can pin the order without a socket or a DOM.
 */

export type SlideType =
  | 'WELCOME'
  | 'RULES'           // house rules between welcome and rounds
  | 'ROUNDS_OVERVIEW' // table of contents: every round, in order
  | 'ROUND_INTRO'
  | 'QUESTION'
  | 'ANSWER'
  | 'ROUND_SUMMARY'   // end-of-round recap. Answers reveal on host action.
  | 'STANDINGS'
  | 'VISUAL_ROUND'
  | 'SONG_BONUS'
  | 'DRAWING'         // optional random raffle near end of night
  | 'OUTRO'           // schedule + socials QR codes at the end of night
  | 'CUSTOM';         // host-made slide from the slide library

export type CustomSlideKind = 'IMAGE' | 'TEXT' | 'SCHEDULE' | 'PROMO';
export type SlideAnchor = 'START' | 'BEFORE_ROUND' | 'AFTER_ROUND' | 'HALFTIME' | 'END';
export type SlideSlot = 'START' | 'BEFORE_ROUND' | 'AFTER_ROUND' | 'END';

/** A public-schedule row as a SCHEDULE slide receives it. */
export interface ScheduleEntry {
  id: number;
  name: string;
  date: string | null;
  type: string;
  theme: string | null;
  recurringDay: string | null;
  recurringTime: string | null;
  recurringShowWeeks: number | null;
}

/**
 * A custom slide as the server resolves it. `slot` and `roundIdx` are the
 * concrete position. The server turns HALFTIME and unknown rounds into one
 * of the four slots, so the client never guesses.
 */
export interface CustomSlide {
  id: string; // placement id
  kind: CustomSlideKind;
  title: string | null;
  body: string | null;
  imageUrl: string | null;
  url: string | null;
  caption: string | null;
  anchor: SlideAnchor;
  roundNumber: number | null;
  sortOrder: number;
  slot: SlideSlot;
  roundIdx: number;
  fellBack: boolean;
  schedule?: ScheduleEntry[];
  // Host payload only (GET /api/events/:id).
  templateId?: string;
  name?: string;
}

export interface SeqEntry {
  slideType: SlideType;
  roundIdx: number;
  questionIdx: number;
  // Placement id. Set only on CUSTOM entries.
  customId?: string;
}

export interface SequenceRound {
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  questions: { type: 'TEXT' | 'SONG' | 'IMAGE' }[];
}

export interface SequenceInput {
  rounds: SequenceRound[];
  rulesText: string | null | undefined;
  showDrawingSlide?: boolean;
  drawingAfterRound?: number | null;
  showOutroSlide?: boolean;
  customSlides?: CustomSlide[];
}

/**
 * Build the sequence. Slide kind comes from the question's type, not the
 * round's, so a STANDARD round can mix TEXT, SONG and IMAGE questions.
 *
 * Custom slides go in at their slot:
 *   START           after WELCOME and RULES, before ROUNDS_OVERVIEW
 *   BEFORE_ROUND i  before round i's ROUND_INTRO
 *   AFTER_ROUND i   after round i's STANDINGS
 *   END             after the final STANDINGS, before OUTRO
 * Slides at one slot keep the order the server sent.
 */
export function buildSlideSequence(input: SequenceInput): SeqEntry[] {
  const { rounds } = input;
  const custom = input.customSlides ?? [];
  const seq: SeqEntry[] = [];

  const pushCustom = (slot: SlideSlot, ri: number) => {
    for (const s of custom) {
      if (s.slot !== slot) continue;
      if ((slot === 'BEFORE_ROUND' || slot === 'AFTER_ROUND') && s.roundIdx !== ri) continue;
      seq.push({ slideType: 'CUSTOM', roundIdx: ri, questionIdx: 0, customId: s.id });
    }
  };

  seq.push({ slideType: 'WELCOME', roundIdx: 0, questionIdx: 0 });

  // Empty or null rules text drops the rules slide.
  if (input.rulesText && input.rulesText.trim()) {
    seq.push({ slideType: 'RULES', roundIdx: 0, questionIdx: 0 });
  }

  pushCustom('START', 0);

  if (rounds.length > 0) {
    seq.push({ slideType: 'ROUNDS_OVERVIEW', roundIdx: 0, questionIdx: 0 });
  }

  for (let ri = 0; ri < rounds.length; ri++) {
    const round = rounds[ri]!;
    pushCustom('BEFORE_ROUND', ri);
    seq.push({ slideType: 'ROUND_INTRO', roundIdx: ri, questionIdx: 0 });

    // VISUAL rounds show every question on one slide (the visual grid).
    // Other rounds get one slide per question, sized to its type.
    if (round.roundType === 'VISUAL') {
      seq.push({ slideType: 'VISUAL_ROUND', roundIdx: ri, questionIdx: 0 });
      // A song question can sit in any round, including a visual round.
      // It still needs its own slide so the clip can play.
      round.questions.forEach((q, qi) => {
        if (q.type === 'SONG') seq.push({ slideType: 'SONG_BONUS', roundIdx: ri, questionIdx: qi });
      });
    } else {
      for (let qi = 0; qi < round.questions.length; qi++) {
        const q = round.questions[qi]!;
        const slideType: SlideType =
          q.type === 'SONG'  ? 'SONG_BONUS' :
          q.type === 'IMAGE' ? 'VISUAL_ROUND' :
                               'QUESTION';
        seq.push({ slideType, roundIdx: ri, questionIdx: qi });
      }
    }

    // End-of-round recap for any round with at least one question.
    if (round.questions.length > 0) {
      seq.push({ slideType: 'ROUND_SUMMARY', roundIdx: ri, questionIdx: 0 });
    }

    // Drawing slot, before this round's standings so the winner can be
    // highlighted on the leaderboard.
    //   drawingAfterRound = null → after the last round (default)
    //   drawingAfterRound = N    → after round N's summary
    //   showDrawingSlide = false → never
    const drawingHere = (() => {
      if (input.showDrawingSlide === false) return false;
      const n = input.drawingAfterRound;
      if (n != null && n > 0) return ri === n - 1;
      return ri === rounds.length - 1;
    })();
    if (drawingHere) {
      seq.push({ slideType: 'DRAWING', roundIdx: ri, questionIdx: 0 });
    }
    seq.push({ slideType: 'STANDINGS', roundIdx: ri, questionIdx: 0 });
    pushCustom('AFTER_ROUND', ri);
  }

  pushCustom('END', Math.max(0, rounds.length - 1));

  // Outro: schedule + socials QR codes. Always last when enabled.
  if (rounds.length > 0 && input.showOutroSlide !== false) {
    seq.push({ slideType: 'OUTRO', roundIdx: rounds.length - 1, questionIdx: 0 });
  }

  return seq;
}

/** Where the host is now, as the host panel tracks it. */
export interface SequencePosition {
  slideType: SlideType;
  currentRound: number;
  currentQuestion: number;
  customSlideId?: string | null;
}

/**
 * Index of the current slide in `seq`, or -1.
 * ANSWER is a host-side state on top of a QUESTION slide, so it matches its
 * QUESTION entry. QUESTION, SONG_BONUS and per-question VISUAL_ROUND
 * entries match on round and question. CUSTOM entries match on placement
 * id. All others match on round only.
 */
export function findSequenceIndex(
  seq: SeqEntry[],
  pos: SequencePosition,
  rounds: SequenceRound[],
): number {
  const effectiveType: SlideType = pos.slideType === 'ANSWER' ? 'QUESTION' : pos.slideType;
  if (effectiveType === 'CUSTOM') {
    return seq.findIndex((s) => s.slideType === 'CUSTOM' && s.customId === pos.customSlideId);
  }
  const isPerQuestionSlide =
    effectiveType === 'QUESTION' ||
    effectiveType === 'SONG_BONUS' ||
    (effectiveType === 'VISUAL_ROUND' && rounds[pos.currentRound]?.roundType !== 'VISUAL');
  return seq.findIndex(
    (s) =>
      s.slideType === effectiveType &&
      s.roundIdx === pos.currentRound &&
      (isPerQuestionSlide ? s.questionIdx === pos.currentQuestion : true),
  );
}

/**
 * Merge a live update of the custom-slide list into the list the host
 * panel shows, so the host's position does not jump during a show.
 *
 * The host position is by identity (slide kind, round, question, or
 * placement id), not by index. So a slide added or removed elsewhere
 * never moves the host. Next still goes to the slide that followed the
 * current one, and nothing is skipped or shown twice.
 *
 * One case needs help. If the slide on screen is removed, or moved to a
 * new position, it has no place in the new list, and Next and Prev would
 * lose their anchor. This keeps that slide at its old place until the host
 * leaves it. After that, a call with the new `currentId` drops it.
 */
export function mergeCustomSlides(
  prev: CustomSlide[],
  next: CustomSlide[],
  currentId: string | null | undefined,
): CustomSlide[] {
  if (!currentId) return next;
  const oldIdx = prev.findIndex((s) => s.id === currentId);
  if (oldIdx === -1) return next;
  const old = prev[oldIdx]!;
  const fresh = next.find((s) => s.id === currentId);
  if (fresh && fresh.slot === old.slot && fresh.roundIdx === old.roundIdx) return next;

  // Keep the old position. Take the new content if the slide still exists.
  const kept: CustomSlide = fresh ? { ...fresh, slot: old.slot, roundIdx: old.roundIdx } : old;
  const rest = next.filter((s) => s.id !== currentId);
  const sameSlot = (s: CustomSlide) => s.slot === old.slot && s.roundIdx === old.roundIdx;

  // Put it after the nearest earlier slide of the same slot that survived.
  for (let j = oldIdx - 1; j >= 0 && sameSlot(prev[j]!); j--) {
    const k = rest.findIndex((s) => s.id === prev[j]!.id && sameSlot(s));
    if (k !== -1) return [...rest.slice(0, k + 1), kept, ...rest.slice(k + 1)];
  }
  // Else first in its slot, so every other slide of the slot comes after it.
  const first = rest.findIndex(sameSlot);
  if (first !== -1) return [...rest.slice(0, first), kept, ...rest.slice(first)];
  return [...rest, kept];
}

/**
 * Step from `idx` by `dir` (+1 Next, -1 Prev), skipping hidden entries.
 * Returns the new index, or -1 when there is nowhere to go. Prev stops at
 * the first entry (WELCOME) even when it is hidden, as before.
 */
export function stepSequence(
  seq: SeqEntry[],
  idx: number,
  dir: 1 | -1,
  isHidden: (e: SeqEntry) => boolean = () => false,
): number {
  let i = idx;
  if (dir === 1) {
    do { i++; } while (i < seq.length && isHidden(seq[i]!));
    return i < seq.length ? i : -1;
  }
  do { i--; } while (i > 0 && isHidden(seq[i]!));
  return i >= 0 ? i : -1;
}
